import { useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { useAdminDashboardStats } from '../apiClient/fetchAdminDashboard';
import { useAppContext } from '../utils/AppContext';
import AdminChoroplethMap from '../components/admin/AdminChoroplethMap';
import AdminStatCard from '../components/admin/AdminStatCard';
import {
  COUNTRIES_GEOJSON_URL,
  filterUsStateFeatures,
  joinRegionCounts,
  US_STATES_GEOJSON_URL,
} from '../utils/adminChoropleth';
import './AdminDashboardPage.css';

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function plural(n: number, singular: string, pluralForm?: string): string {
  const word = n === 1 ? singular : (pluralForm ?? `${singular}s`);
  return `${formatNumber(n)} ${word}`;
}

const ALL_CAMPAIGNS = 'all';

export default function AdminDashboardPage() {
  const context = useAppContext();
  const [selectedCampaignId, setSelectedCampaignId] =
    useState<string>(ALL_CAMPAIGNS);
  const { data, isLoading, isError, error, isFetching } =
    useAdminDashboardStats(selectedCampaignId);

  const [countriesGeoJson, setCountriesGeoJson] =
    useState<GeoJSON.FeatureCollection | null>(null);
  const [statesGeoJson, setStatesGeoJson] =
    useState<GeoJSON.FeatureCollection | null>(null);
  const [boundariesLoading, setBoundariesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [countriesRes, statesRes] = await Promise.all([
          fetch(COUNTRIES_GEOJSON_URL),
          fetch(US_STATES_GEOJSON_URL),
        ]);
        const countries = await countriesRes.json();
        const states = await statesRes.json();
        if (!cancelled) {
          setCountriesGeoJson(countries);
          setStatesGeoJson(filterUsStateFeatures(states));
        }
      } catch (e) {
        console.error('Failed to load boundary GeoJSON', e);
      } finally {
        if (!cancelled) {
          setBoundariesLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const countryChoropleth = useMemo(() => {
    if (!countriesGeoJson || !data) return null;
    return joinRegionCounts(
      countriesGeoJson,
      data.collections_by_country,
      (props) => {
        const code = props?.ISO_A2 || props?.iso_a2;
        return code && code !== '-99' ? String(code) : undefined;
      },
    );
  }, [countriesGeoJson, data]);

  const stateChoropleth = useMemo(() => {
    if (!statesGeoJson || !data) return null;
    return joinRegionCounts(
      statesGeoJson,
      data.collections_by_us_state,
      (props) => {
        const postal = props?.postal || props?.POSTAL;
        if (postal) return String(postal);
        const iso = props?.iso_3166_2 || props?.ISO_3166_2;
        if (iso && String(iso).startsWith('US-')) {
          return String(iso).slice(3);
        }
        return undefined;
      },
    );
  }, [statesGeoJson, data]);

  if (isLoading || boundariesLoading) {
    return (
      <div className="d-flex justify-content-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Alert variant="danger" className="m-4">
        Failed to load dashboard statistics:{' '}
        {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  const mapUpdatedLabel = data.computed_at
    ? new Date(data.computed_at).toLocaleString()
    : 'Never';

  const { platform } = data;
  const showStaffMetrics = data.is_staff && platform.users != null;

  return (
    <div className="container-fluid py-4 admin-dashboard">
      <div className="admin-dashboard__header">
        <div className="admin-dashboard__header-main">
          <h2>SigCap Statistics</h2>
          <p className="admin-dashboard__meta">
            Map data last rebuilt {mapUpdatedLabel}
          </p>
        </div>
        <div className="admin-dashboard__campaign-filter">
          <Form.Label htmlFor="admin-campaign-filter" className="mb-1">
            Campaign
          </Form.Label>
          <Form.Select
            id="admin-campaign-filter"
            value={selectedCampaignId}
            onChange={(e) => setSelectedCampaignId(e.target.value)}
            disabled={isFetching && !isLoading}
            aria-label="Filter statistics by campaign"
          >
            <option value={ALL_CAMPAIGNS}>All data</option>
            {data.campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label} ({formatNumber(c.collection_count)})
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      {data.stale && (
        <Alert variant="warning" className="mb-4">
          Geographic statistics are being recalculated. Map colors may be
          outdated until the next rebuild completes.
        </Alert>
      )}

      <div
        className={`admin-dashboard__infographic-row${isFetching && !isLoading ? ' admin-dashboard__infographic-row--loading' : ''}`}
      >
          <AdminStatCard
            variant="cyan"
            label="Captures"
            value={formatNumber(data.totals.captures)}
            subtitle={plural(data.totals.collections, 'collection')}
            icon={<FolderOutlinedIcon fontSize="inherit" />}
          />
          {showStaffMetrics && (
            <AdminStatCard
              variant="green"
              label="Registered users"
              value={formatNumber(platform.users!.total)}
              subtitle={
                platform.users!.new_last_30_days === 1
                  ? '1 account created this month'
                  : `${formatNumber(platform.users!.new_last_30_days)} accounts created this month`
              }
              icon={<PeopleOutlineIcon fontSize="inherit" />}
            />
          )}
          <AdminStatCard
            variant="sunset"
            label={showStaffMetrics ? 'Download requests' : 'Your download requests'}
            value={formatNumber(platform.exports.total_requests)}
            subtitle={`${formatNumber(platform.exports.last_30_days)} in the last 30 days`}
            icon={<CloudDownloadIcon fontSize="inherit" />}
          />
      </div>

      <section className="admin-dashboard__section">
        <p className="admin-dashboard__section-title">Geographic distribution</p>
        <p className="admin-dashboard__map-caption">
          Color reflects collections whose plurality of capture locations fall in
          each region. Click a region for its count.
          {selectedCampaignId !== ALL_CAMPAIGNS && data.campaigns.length > 0 && (
            <>
              {' '}
              Showing campaign:{' '}
              <strong>
                {
                  data.campaigns.find((c) => c.id === selectedCampaignId)
                    ?.label
                }
              </strong>
              .
            </>
          )}
        </p>

        <Row className="g-4">
          <Col lg={6}>
            <div className="admin-dashboard__map-panel">
              <h5>By country</h5>
              <AdminChoroplethMap
                id="world"
                geoJson={countryChoropleth}
                center={[20, 0]}
                zoom={1}
                regionKind="Country"
                height="420px"
              />
            </div>
          </Col>
          <Col lg={6}>
            <div className="admin-dashboard__map-panel">
              <h5>By US state</h5>
              <AdminChoroplethMap
                id="us"
                geoJson={stateChoropleth}
                center={[39, -98]}
                zoom={3}
                regionKind="US state"
                height="420px"
              />
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
}
